import { useMutation, useQueryClient } from '@tanstack/react-query';

import { localStorage } from '@change-corgi/core/storage';
import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import {
	DraftClient,
	findOrCreateDraftAuthorId,
	InMemoryAdapter,
	LocalStorageAdapter,
} from '#src/app/shared/api/petitionDraft';
import { formatDescriptionForSapEditor } from '#src/app/shared/utils/formatDescriptionForSapEditor';

import { generateAiDraftFromSocialPost } from './api/api';
import type { GenerateAiDraftFromSocialPostErrorReason } from './api/api.graphql';
import { generateAiPetitionImage } from './api/generateAiPetitionImage';

// Bounds the tracked `reason` to the server's GenerateAiDraftFromSocialPostErrorReason enum so
// transport-error messages don't leak in as ad-hoc values. The Record is keyed by the generated
// enum so a schema change that adds or renames a reason fails typecheck here instead of
// silently reporting OTHER.
const KNOWN_GENERATION_ERROR_REASONS_MAP: Record<GenerateAiDraftFromSocialPostErrorReason, true> = {
	FLAGGED_CONTENT: true,
	INVALID_JSON_RESPONSE: true,
	NOT_FOUND: true,
	OTHER: true,
	RATE_LIMIT: true,
	SOCIAL_POST_UNAVAILABLE: true,
	UNAUTHORIZED: true,
};
const KNOWN_GENERATION_ERROR_REASONS: ReadonlySet<string> = new Set(Object.keys(KNOWN_GENERATION_ERROR_REASONS_MAP));

export function useGenerateSocialPostDraft() {
	const navigate = useNavigate();
	const track = useTracking();
	const utilityContext = useUtilityContext();
	const queryClient = useQueryClient();
	const draftAuthorId = findOrCreateDraftAuthorId({ storage: localStorage, queryClient });

	const draftClient = new DraftClient([new LocalStorageAdapter(localStorage), new InMemoryAdapter(queryClient)]);

	return useMutation({
		mutationFn: async (input: { url: string; caption: string | undefined }) => {
			const result = await generateAiDraftFromSocialPost(input, utilityContext);

			if (result.__typename !== 'GenerateAiDraftFromSocialPostSuccess') {
				throw new Error(result.reason);
			}

			// Kick off the background AI image job before persisting, so SAP's preview step finds
			// the job id in the draft metadata and polls it to completion (see useImageJob in
			// startAPetitionV2 — it only resumes jobs, it never starts one for a prefilled draft).
			// A null job (image service failure) still creates the draft, just without an image.
			const imageJob = await generateAiPetitionImage({ title: result.ask }, utilityContext);

			// recommendedDraft is what tags the petition ai_ask/ai_description at publish;
			// petitionDraft starts as a copy of it (matching useSocialEntry in startAPetitionV2).
			const recommendedDraft = {
				ask: result.ask,
				description: formatDescriptionForSapEditor(result.description),
				image: null,
			};

			return draftClient.createDraft(draftAuthorId, {
				type: 'ai_draft',
				recommendedDraft,
				petitionDraft: recommendedDraft,
				metadata: {
					imageJobId: imageJob?.responseId ?? null,
					imageGenerationCount: imageJob ? 1 : 0,
				},
			});
		},
		onSuccess: (_data, { url }) => {
			// Completes the funnel opposite homepage_social_post_generation_error: fired once the
			// draft is generated, before the redirect, so click → success/error is measurable.
			// Tracing back to the LangSmith trace happens via `source`, echoed by the prompt itself
			// into its own output — see executePrompt.ts — not through anything tracked here.
			void track('homepage_social_post_generation_success', { source: url });
			const sapParams = new URLSearchParams({ source_location: 'homepage_tiktok' });
			navigate(`/start-a-petition/intro?${sapParams.toString()}`);
		},
		onError: (error: Error, { url }) => {
			// Distinct from the freeform path's event: carries the server reason so we can see
			// which generation failures happen in the wild. The thrown message is the reason
			// enum for GraphQL errors; anything else (e.g. a transport failure) reports OTHER.
			const reason = KNOWN_GENERATION_ERROR_REASONS.has(error.message) ? error.message : 'OTHER';
			void track('homepage_social_post_generation_error', { reason, source: url });
		},
	});
}
